import { useIFetch } from '@/composables/useIFetch';
import apiRoutes from '@/const/apiRoutes';
import type { ClientInfo, ClientProfile } from '@/types/clientInformation';
import type { Education, License } from '@/types/therapistInformation';
import { computed } from 'vue';
import { type RegisterStep } from '~/composables/useRegisterSteps';
import type { TherapistInformation } from '~/types/therapistInformation';

export const useClientInfo = defineStore('clientInfo', () => {
   const { identify } = useSegment();
   const { setUser } = useSentry();
   const route = computed(() => useRoute());
   const { setSnackbarDetail } = useSnackbarStore();
   const clientInformation: Ref<ClientInfo | null> = ref(null);
   const currentTherapistDetail: Ref<TherapistInformation | null> = ref(null);
   const therapistEducationList: Ref<Education[]> = ref([]);
   const therapistLicenseList: Ref<License[]> = ref([]);
   const clientRole: Ref<any | null> = ref(null); //to add type
   const { switchStepBasedByData, currentRegisterStep } = useRegisterSteps();
   const clientProfile: Ref<ClientProfile | null> = ref(null);
   const needToChangeTimeZone = ref(false);
   const isClient = computed(() => {
      return route?.value?.meta?.layout == 'client';
   });
   const clientHasSubscription = computed(() => {
      return clientInformation.value?.client?.has_subscription;
   });
   const clientBenefitDetail = computed(() => {
      return clientInformation.value?.client?.subscriptions;
   });
   const fromProfileChangeTherapist = ref(false);
   const clientImage: Ref<string | null> = ref(clientProfile?.value?.image || null);
   const setRedirectionUrl = (detail: boolean) => {
      fromProfileChangeTherapist.value = detail;
   };
   const status: Ref<{
      statusCode?: number;
      route?: RegisterStep;
   } | null> = ref(null);
   const getClientRole = async () => {
      try {
         const { data, error } = await useIFetch(apiRoutes?.auth?.userRole);
         clientRole.value = data.value;
         if (error.value) {
            setSnackbarDetail({
               show: true,
               title: 'Something went wrong',
               type: 'error',
            });
         }
      } catch (e: any) {
         setSnackbarDetail({
            show: true,
            title: e?.message,
            type: 'error',
         }); //change later to use snackbar store(need to create)
      }
   };
   const therapistStatus = ref(null);
   const therapistReassign = ref(false);
   const setTherapistReassign = (payload: boolean) => {
      therapistReassign.value = payload;
   };
   const getTherapistStatus = async () => {
      try {
         const { data, error } = await useIFetch(apiRoutes?.client?.therapistStatus);
         therapistStatus.value = data.value;
         if (error.value) {
            setSnackbarDetail({
               show: true,
               title: 'Something went wrong',
               type: 'error',
            });
         }
      } catch (e: any) {
         setSnackbarDetail({
            show: true,
            title: e?.message,
            type: 'error',
         }); //change later to use snackbar store(need to create)
      }
   };
   const changeStatusCode = () => {
      status.value = {
         statusCode: 1,
         route: undefined,
      };
   };

   const updateStatus = () => {
      const to = switchStepBasedByData();
      status.value = {
         statusCode: to ? 0 : 1,
         route: to ?? undefined,
      };
   };
   const updateClientImage = (file_id: string) => {
      clientImage.value = file_id;
      if (clientProfile.value) clientProfile.value.image = file_id;
   };

   const getClientInfo = async ({
      silent = false,
      expectedAccessToken,
   }: { silent?: boolean; expectedAccessToken?: string } = {}) => {
      const expectedTokenRef =
         expectedAccessToken !== undefined ? useAuthClientStorage().clientToken : null;
      const { data, error } = await useIFetch<ClientProfile | null>(
         apiRoutes?.client?.profile?.get,
      );
      if (
         expectedAccessToken !== undefined &&
         expectedTokenRef?.value?.access_token !== expectedAccessToken
      )
         return false;
      clientProfile.value = data?.value;
      clientImage.value = data?.value?.image || null;
      updateStatus();
      if (data.value) {
         const detectedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
         needToChangeTimeZone.value = data?.value?.timezone != detectedTimeZone;
         setUser({ id: data.value.id });
         identify({ id: data.value.id, email: data.value.email });
      }
      if (error?.value && !silent) {
         setSnackbarDetail({
            show: true,
            title: 'Something went wrong',
            type: 'error',
         });
      }
      return !!data?.value?.id && !error?.value;
   };
   const updateClientInfo = async (formData: any) => {
      const { error, data } = await useIFetch(apiRoutes.client.profile.update, {
         body: formData,
         method: 'PUT',
      });
      if (error?.value) {
         setSnackbarDetail({
            show: true,
            title: error?.value?.data?.message,
            type: 'error',
         });
      } else {
         await getClientInfo();
      }
      return { data, error };
   };
   const updateTeenEmergencyDetail = async (formData: any) => {
      const { error, data } = await useIFetch(apiRoutes.client.emergency.detail, {
         body: formData,
         method: 'POST',
      });
      if (error?.value) {
         setSnackbarDetail({
            show: true,
            title: error?.value?.data?.message,
            type: 'error',
         });
      } else {
         await getClientInfo();
      }
      return { data, error };
   };

   // Reads the client's own guardian record. The endpoint answers 200 with a
   // null `data` when none exists, which is the ordinary "not set up yet" case
   // rather than an error.
   const getGuardianContact = async () => {
      // Explicit key: useIFetch derives its key from the URL alone, and this
      // GET shares a URL with the guardian POST above. Without a distinct key
      // both share one asyncData entry, and `dedupe: 'cancel'` would let an
      // overlapping pair cancel one and leave whichever finished last in the
      // shared data ref. The read now runs from both mount and after a save,
      // so an overlap is reachable.
      const { error, data } = await useIFetch<{ data: any }>(apiRoutes.client.emergency.detail, {
         method: 'GET',
         key: 'client-guardian-contact-get',
      });
      return { guardian: (data?.value as any)?.data ?? null, error };
   };

   const deductClientCredit = () => {
      let credit = clientInformation?.value?.client?.credits;
      if (clientInformation.value) clientInformation.value.client.credits = credit ? credit - 1 : 0;
   };
   let selectedTherapistRequest = 0;
   const getSelectedTherapistInfo = async ({
      expectedAccessToken,
   }: { expectedAccessToken?: string } = {}) => {
      const { clientToken } = useAuthClientStorage();
      const ownerToken = expectedAccessToken ?? clientToken.value?.access_token;
      if (!ownerToken || clientToken.value?.access_token !== ownerToken) return false;
      const request = ++selectedTherapistRequest;
      const { data, error } = await useIFetch<ClientInfo>(apiRoutes?.client?.sessions?.info, {
         key: `client-session-info-${request}`,
      });
      if (clientToken.value?.access_token !== ownerToken || request !== selectedTherapistRequest)
         return false;
      if (error?.value) return false;
      if (data?.value) {
         clientInformation.value = data?.value;
      } else {
         clientInformation.value = null;
      }
      return !!data?.value;
   };
   const loading = ref(true);
   const getCurrentTherapistDetail = async () => {
      const { data } = await useIFetch<TherapistInformation>(apiRoutes?.client?.selectedTherapist);
      if (data?.value) {
         currentTherapistDetail.value = data?.value;
         loading.value = false;
      } else {
         currentTherapistDetail.value = null;
      }
   };
   const getTherapistEducationLicenses = async () => {
      const { data } = await useIFetch<{
         educations: Education[];
         licenses: License[];
      }>(apiRoutes?.client?.therapistEducationLicense);
      if (data?.value) {
         therapistLicenseList.value = data?.value?.licenses;
         therapistEducationList.value = data?.value?.educations;
         loading.value = false;
      } else {
         therapistLicenseList.value = [];
         therapistEducationList.value = [];
      }
   };
   const clearTherapistEducationLicenses = () => {
      therapistLicenseList.value = [];
      therapistEducationList.value = [];
   };
   return {
      getClientInfo,
      clientProfile,
      setRedirectionUrl,
      updateTeenEmergencyDetail,
      getGuardianContact,
      fromProfileChangeTherapist,
      clientRole,
      getClientRole,
      deductClientCredit,
      clearTherapistEducationLicenses,
      currentTherapistDetail,
      loading,
      updateClientImage,
      clientImage,
      getCurrentTherapistDetail,
      getTherapistEducationLicenses,
      therapistEducationList,
      therapistLicenseList,
      getSelectedTherapistInfo,
      clientInformation,
      updateClientInfo,
      clientHasSubscription,
      clientBenefitDetail,
      isClient,
      status,
      updateStatus,
      changeStatusCode,
      needToChangeTimeZone,
      therapistStatus,
      getTherapistStatus,
      therapistReassign,
      setTherapistReassign,
   };
});
